import { IconButtonProps } from '@lib/uikit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Player from 'video.js/dist/types/player';

import {
    useProgressContext,
    useProgressDispatchContext,
} from '../contexts/ProgressContext';
import { SanitizedStoryPage } from '../types';
import { useVideoAnalytics } from './useVideoAnalytics';

export type UseVideoPlayerProps = {
    storyPage: SanitizedStoryPage;
    isCurrent: boolean;
};

export type UseVideoPlayerReturn = {
    isMuted: boolean;
    buttons: IconButtonProps[];
    playVideo: () => void;
    pauseVideo: () => void;
    onPlayerReady: (player: Player) => void;
};

const useVideoPlayer = ({
    storyPage,
    isCurrent,
}: UseVideoPlayerProps): UseVideoPlayerReturn => {
    const player = useRef<Player | null>(null);
    const [hasAudio, setHasAudio] = useState<boolean>(true);
    const [isMuted, setIsMuted] = useState<boolean>(storyPage.muted);
    const { isPaused } = useProgressContext();
    const dispatch = useProgressDispatchContext();
    const { videoAnalytics } = useVideoAnalytics(
        player.current?.tech(true).el() as HTMLVideoElement | undefined
    );

    const muteVideo = useCallback(() => {
        player.current?.muted(true);
        player.current?.trigger('mute');
        setIsMuted(true);
    }, []);

    const unmuteVideo = useCallback(() => {
        player.current?.muted(false);
        setIsMuted(false);
        player.current?.trigger('unmute');
    }, []);

    const pauseVideo = useCallback(
        (pauseUI = true) => {
            if (!player.current) return;
            player.current.pause();
            if (pauseUI) dispatch({ type: 'PAUSE' });
        },
        [dispatch]
    );

    const playVideo = useCallback(async () => {
        if (!player.current) return;

        try {
            const currentTime = player.current.currentTime()!;
            await player.current.play();

            if (currentTime > 0) {
                dispatch({ type: 'RESUME' });
            } else {
                dispatch({
                    type: 'START',
                    payload: {
                        duration: player.current.duration()!,
                        isInfinite: storyPage.looping,
                    },
                });
            }
        } catch (error) {
            console.warn('[Play failed]:', error);
            videoAnalytics?.sendMediaEvent('autoplay fail');

            if (!player.current.muted()) {
                muteVideo();
                await playVideo();
            } else {
                pauseVideo();
            }
        }
    }, [storyPage, videoAnalytics, dispatch, muteVideo, pauseVideo]);

    const resetVideo = useCallback(
        (resetUI = true) => {
            if (!player.current) return;
            player.current.currentTime(0);
            if (resetUI) dispatch({ type: 'RESET' });
        },
        [dispatch]
    );

    const restartVideo = useCallback(() => {
        resetVideo();
        playVideo();
        dispatch({ type: 'RESTART' });
    }, [dispatch, playVideo, resetVideo]);

    const deactivateBackgroundVideo = useCallback(() => {
        resetVideo(false);
        pauseVideo(false);
    }, [pauseVideo, resetVideo]);

    const checkAudioTracks = useCallback(() => {
        if (!player.current) return;

        const videoElement = player.current.tech(true).el() as HTMLVideoElement;
        // @ts-expect-error
        const audioTracks = videoElement.audioTracks;

        const detectAudio = (): boolean => {
            const detected =
                // @ts-expect-error
                videoElement.mozHasAudio ??
                // @ts-expect-error
                videoElement.webkitAudioDecodedByteCount > 0;

            setHasAudio(detected);
            return detected;
        };

        if (audioTracks && audioTracks.length > 0) {
            setHasAudio(true);
        } else {
            const detected = detectAudio();

            if (!detected) {
                setTimeout(() => {
                    detectAudio();
                }, 200);
            }
        }
    }, []);

    const onPlayerReady = useCallback(
        (p: Player) => {
            player.current = p;
            if (isCurrent) playVideo();
        },
        [isCurrent, playVideo]
    );

    const onCanPlay = useCallback(() => {
        checkAudioTracks();
    }, [checkAudioTracks]);

    const onCanPlayThrough = useCallback(() => {
        if (!isPaused && isCurrent && player.current?.paused()) {
            playVideo();
        }
    }, [isCurrent, isPaused, playVideo]);

    const onEnded = useCallback(() => {
        if (storyPage.looping) {
            restartVideo();
        }
    }, [storyPage.looping, restartVideo]);

    const onDispose = useCallback(() => {
        player.current = null;
    }, []);

    const onPlaying = useCallback(() => {
        if (isCurrent) {
            dispatch({ type: 'RESUME' });
        }
    }, [isCurrent, dispatch]);

    const onWaiting = useCallback(() => {
        if (isCurrent) {
            dispatch({ type: 'PAUSE', payload: { animationOnly: true } });
        }
    }, [isCurrent, dispatch]);

    useEffect(() => {
        const p = player.current;
        if (!p) return;

        p.on('canplay', onCanPlay);
        p.on('canplaythrough', onCanPlayThrough);
        p.on('ended', onEnded);
        p.on('dispose', onDispose);
        p.on('playing', onPlaying);
        p.on('waiting', onWaiting);

        return () => {
            p.off('canplay', onCanPlay);
            p.off('canplaythrough', onCanPlayThrough);
            p.off('ended', onEnded);
            p.off('dispose', onDispose);
            p.off('playing', onPlaying);
            p.off('waiting', onWaiting);
        };
    }, [onCanPlay, onCanPlayThrough, onEnded, onDispose, onPlaying, onWaiting]);

    useEffect(() => {
        if (isCurrent) {
            restartVideo();
        } else {
            deactivateBackgroundVideo();
        }
    }, [isCurrent]);

    const buttons: IconButtonProps[] = useMemo(() => {
        const _buttons: IconButtonProps[] = [];

        _buttons.push({
            ariaLabel: isPaused ? 'Play Video' : 'Pause Video',
            icon: isPaused ? 'play_arrow' : 'pause',
            onClick: () => (isPaused ? playVideo() : pauseVideo()),
        });

        if (hasAudio) {
            _buttons.push({
                ariaLabel: isMuted ? 'Unmute Video' : 'Mute Video',
                icon: !isMuted ? 'volume_up' : 'volume_off',
                onClick: () => {
                    if (isMuted) {
                        unmuteVideo();
                    } else {
                        muteVideo();
                    }
                },
            });
        }

        return _buttons;
    }, [
        isPaused,
        isMuted,
        hasAudio,
        muteVideo,
        unmuteVideo,
        playVideo,
        pauseVideo,
    ]);

    return {
        isMuted,
        buttons,
        playVideo,
        pauseVideo,
        onPlayerReady,
    };
};

export default useVideoPlayer;
